/**
 * 邮件模板服务 V2
 * 对接邮件模板 V2 API
 */
import { apiRequest } from '@/utils/api'

class EmailTemplateService {
  constructor() {
    this.baseURL = '/email-templates/api/v2'
  }

  /**
   * 获取模板分类列表
   * @param {Object} params - 查询参数
   * @returns {Promise}
   */
  async getCategories(params = {}) {
    try {
      const response = await apiRequest.get(`${this.baseURL}/categories/`, params)
      return {
        success: true,
        results: response.data.results || response.data,
        count: response.data.count || response.data.length
      }
    } catch (error) {
      console.error('获取分类失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取分类失败'
      }
    }
  }

  /**
   * 创建模板分类
   * @param {Object} categoryData - 分类数据
   * @returns {Promise}
   */
  async createCategory(categoryData) {
    try {
      const response = await apiRequest.post(`${this.baseURL}/categories/`, categoryData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('创建分类失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '创建分类失败',
        errors: error.response?.data?.errors || error.response?.data,
        fieldErrors: error.response?.data // 字段级错误
      }
    }
  }

  /**
   * 更新模板分类
   * @param {Number} categoryId - 分类ID
   * @param {Object} categoryData - 分类数据
   * @returns {Promise}
   */
  async updateCategory(categoryId, categoryData) {
    try {
      const response = await apiRequest.put(`${this.baseURL}/categories/${categoryId}/`, categoryData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('更新分类失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '更新分类失败',
        errors: error.response?.data?.errors || error.response?.data,
        fieldErrors: error.response?.data // 字段级错误
      }
    }
  }

  /**
   * 删除模板分类
   * @param {Number} categoryId - 分类ID
   * @returns {Promise}
   */
  async deleteCategory(categoryId) {
    try {
      await apiRequest.delete(`${this.baseURL}/categories/${categoryId}/`)
      return {
        success: true
      }
    } catch (error) {
      console.error('删除分类失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '删除分类失败'
      }
    }
  }

  /**
   * 获取模板列表
   * @param {Object} params - 查询参数
   * @returns {Promise}
   */
  async getTemplates(params = {}) {
    try {
      const response = await apiRequest.get(`${this.baseURL}/templates/`, params)
      return {
        success: true,
        results: response.data.results || response.data,
        count: response.data.count || response.data.length,
        next: response.data.next,
        previous: response.data.previous
      }
    } catch (error) {
      console.error('获取模板失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取模板失败'
      }
    }
  }

  /**
   * 获取模板详情
   * @param {Number} templateId - 模板ID
   * @returns {Promise}
   */
  async getTemplate(templateId) {
    try {
      const response = await apiRequest.get(`${this.baseURL}/templates/${templateId}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取模板详情失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取模板详情失败'
      }
    }
  }

  /**
   * 创建模板
   * @param {Object} templateData - 模板数据
   * @returns {Promise}
   */
  async createTemplate(templateData) {
    try {
      const response = await apiRequest.post(`${this.baseURL}/templates/`, templateData)
      
      // 检查200响应中是否包含敏感词信息
      if (response.data && response.data.sensitive_word_data && response.data.success === false) {
        // 敏感词检测失败，模拟一个错误响应，让组件层能够处理
        const mockError = {
          response: {
            status: 200,
            data: response.data
          }
        }
        throw mockError
      }
      
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      // 如果是敏感词相关的响应（200状态码但包含敏感词数据）
      if (error.response?.status === 200 && error.response?.data?.sensitive_word_data) {
        // 这是敏感词检测的正常流程，不是真正的错误
        throw error // 重新抛出错误，让组件处理
      }
      
      // 如果是原来的400状态码敏感词警告（兼容性处理）
      if (error.response?.status === 400 && 
          (error.response?.data?.warning || error.response?.data?.sensitive_word_data)) {
        // 这是敏感词检测的正常流程，不是真正的错误
        throw error // 重新抛出错误，让组件处理
      }
      
      // 只有在真正的错误时才打印错误日志
      console.error('创建模板失败:', error)
      
      return {
        success: false,
        message: error.response?.data?.message || '创建模板失败',
        errors: error.response?.data?.errors || error.response?.data,
        fieldErrors: error.response?.data // 字段级错误
      }
    }
  }

  /**
   * 更新模板
   * @param {Number} templateId - 模板ID
   * @param {Object} templateData - 模板数据
   * @returns {Promise}
   */
  async updateTemplate(templateId, templateData) {
    try {
      const response = await apiRequest.put(`${this.baseURL}/templates/${templateId}/`, templateData)
      
      // 检查200响应中是否包含敏感词信息
      if (response.data && response.data.sensitive_word_data && response.data.success === false) {
        // 敏感词检测失败，模拟一个错误响应，让组件层能够处理
        const mockError = {
          response: {
            status: 200,
            data: response.data
          }
        }
        throw mockError
      }
      
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      // 如果是敏感词相关的响应（200状态码但包含敏感词数据）
      if (error.response?.status === 200 && error.response?.data?.sensitive_word_data) {
        // 这是敏感词检测的正常流程，不是真正的错误
        throw error // 重新抛出错误，让组件处理
      }
      
      // 如果是原来的400状态码敏感词警告（兼容性处理）
      if (error.response?.status === 400 && 
          (error.response?.data?.warning || error.response?.data?.sensitive_word_data)) {
        // 这是敏感词检测的正常流程，不是真正的错误
        throw error // 重新抛出错误，让组件处理
      }
      
      // 只有在真正的错误时才打印错误日志
      console.error('更新模板失败:', error)
      
      return {
        success: false,
        message: error.response?.data?.message || '更新模板失败',
        errors: error.response?.data?.errors || error.response?.data,
        fieldErrors: error.response?.data // 字段级错误
      }
    }
  }

  /**
   * 删除模板
   * @param {Number} templateId - 模板ID
   * @returns {Promise}
   */
  async deleteTemplate(templateId) {
    try {
      await apiRequest.delete(`${this.baseURL}/templates/${templateId}/`)
      return {
        success: true
      }
    } catch (error) {
      console.error('删除模板失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '删除模板失败'
      }
    }
  }

  /**
   * 复制模板
   * @param {Number} templateId - 模板ID
   * @returns {Promise}
   */
  async duplicateTemplate(templateId) {
    try {
      const response = await apiRequest.post(`${this.baseURL}/templates/${templateId}/duplicate/`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('复制模板失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '复制模板失败'
      }
    }
  }

  /**
   * 预览模板（变量替换）
   * @param {Number} templateId - 模板ID
   * @param {Object} variables - 变量数据
   * @returns {Promise}
   */
  async previewTemplate(templateId, variables = {}) {
    try {
      const response = await apiRequest.post(`${this.baseURL}/templates/${templateId}/preview/`, {
        variables
      })
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('预览模板失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '预览模板失败'
      }
    }
  }

  /**
   * 增加模板使用次数
   * @param {Number} templateId - 模板ID
   * @returns {Promise}
   */
  async incrementUsage(templateId) {
    try {
      const response = await apiRequest.post(`${this.baseURL}/templates/${templateId}/increment_usage/`)
      return {
        success: true,
        usage_count: response.data.usage_count
      }
    } catch (error) {
      console.error('更新使用次数失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '更新使用次数失败'
      }
    }
  }

  /**
   * 获取市场模板列表
   * @param {Object} params - 查询参数
   * @returns {Promise}
   */
  async getMarketTemplates(params = {}) {
    try {
      const response = await apiRequest.get(`${this.baseURL}/market/templates/`, params)
      return {
        success: true,
        results: response.data.results || response.data,
        count: response.data.count || response.data.length,
        next: response.data.next,
        previous: response.data.previous
      }
    } catch (error) {
      console.error('获取市场模板失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取市场模板失败'
      }
    }
  }

  /**
   * 获取市场模板详情
   * @param {Number} templateId - 模板ID
   * @returns {Promise}
   */
  async getMarketTemplate(templateId) {
    try {
      const response = await apiRequest.get(`${this.baseURL}/market/templates/${templateId}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取市场模板详情失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取市场模板详情失败'
      }
    }
  }

  /**
   * 导入市场模板
   * @param {Number} templateId - 模板ID
   * @returns {Promise}
   */
  async importMarketTemplate(templateId) {
    try {
      const response = await apiRequest.post(`${this.baseURL}/market/templates/${templateId}/import_template/`)
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('导入市场模板失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '导入市场模板失败'
      }
    }
  }

  /**
   * 获取市场模板分类统计
   * @returns {Promise}
   */
  async getMarketTemplateCategories() {
    try {
      const response = await apiRequest.get(`${this.baseURL}/market/templates/categories/`)
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('获取市场模板分类失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取市场模板分类失败'
      }
    }
  }

  /**
   * 检测敏感词
   * @param {Object} data - 检测数据
   * @param {String} data.subject - 邮件主题
   * @param {String} data.body_html - 邮件内容
   * @returns {Promise}
   */
  async checkSensitiveWords(data) {
    try {
      const response = await apiRequest.post(`${this.baseURL}/templates/check_sensitive_words/`, data)
      return {
        success: true,
        data: response.data.data
      }
    } catch (error) {
      console.error('敏感词检测失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '敏感词检测失败'
      }
    }
  }

  /**
   * 上传图片
   * @param {FormData} formData - 图片文件 FormData
   * @returns {Promise}
   */
  async uploadImage(formData) {
    try {
      const response = await apiRequest.post(`${this.baseURL}/templates/upload_image/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return {
        success: true,
        url: response.data.url
      }
    } catch (error) {
      console.error('图片上传失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.detail || '图片上传失败'
      }
    }
  }
}

export default new EmailTemplateService()
